// SigWaz API client
import type {
  ConversionConfig,
  ConvertResponse,
  BatchConvertResponse,
} from "../types/sigwaz";

const BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

function configToRequest(yaml: string, cfg: ConversionConfig) {
  return {
    sigma_yaml: yaml,
    rule_id_start: cfg.ruleIdStart,
    levels: cfg.levels,
    no_full_log: cfg.noFullLog,
    email_alert: cfg.emailAlert,
    email_levels: cfg.emailLevels,
    field_map_overrides: cfg.fieldMapOverrides,
    if_sid_overrides: cfg.ifSidOverrides,
    if_group_overrides: cfg.ifGroupOverrides,
    rules_link_base: cfg.rulesLinkBase,
    // Legacy flag derived from excludedStatuses for backward compat
    process_experimental: !cfg.excludedStatuses.includes("experimental"),
    split_size: cfg.splitSize,
    excluded_statuses: cfg.excludedStatuses,
    min_level: cfg.minLevel,
    allowed_products: cfg.allowedProducts,
  };
}

function _parseErrorDetail(body: unknown, status: number): string {
  if (body && typeof body === "object") {
    const detail = (body as Record<string, unknown>).detail;
    if (Array.isArray(detail)) return detail.join("; ");
    if (typeof detail === "string" && detail) return detail;
  }
  return `Server error (${status})`;
}

async function _throwIfError(res: Response): Promise<void> {
  if (res.ok) return;
  let body: unknown;
  try { body = await res.json(); } catch { /* plain text or empty */ }
  throw new Error(_parseErrorDetail(body, res.status));
}

export async function convertSingle(
  yaml: string,
  cfg: ConversionConfig
): Promise<ConvertResponse> {
  const res = await fetch(`${BASE_URL}/api/convert`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(configToRequest(yaml, cfg)),
  });
  await _throwIfError(res);
  return res.json();
}

export async function convertBatch(
  yaml: string,
  cfg: ConversionConfig
): Promise<BatchConvertResponse> {
  const res = await fetch(`${BASE_URL}/api/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(configToRequest(yaml, cfg)),
  });
  await _throwIfError(res);
  return res.json();
}

export async function getFieldMaps(): Promise<Record<string, Record<string, string>>> {
  const res = await fetch(`${BASE_URL}/api/fieldmaps`);
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const data = await res.json();
  return data.maps ?? {};
}

export async function checkHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE_URL}/health`);
    return res.ok;
  } catch {
    return false;
  }
}
